import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiDollarSign, FiDownload, FiPrinter, FiSearch, FiTrendingUp,
  FiTrendingDown, FiPercent, FiExternalLink, FiBarChart2, FiShoppingCart
} from 'react-icons/fi';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getEmployeesCached, getJobsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import CustomerSelect from '../../components/CustomerSelect';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';
import { getTaxTerms } from '../../utils/taxTerms';

const CHART_COLORS = [
  colors.chart.primary,
  colors.chart.secondary,
  colors.chart.tertiary,
  colors.chart.accent1,
  colors.chart.accent2,
  colors.chart.accent3,
  '#4A90D9',
  '#E67E22',
  '#2ECC71',
  '#9B59B6'
];

const DOC_TYPE_OPTIONS = [
  { code: 'SI', label: 'Tax Sale' },
  { code: 'NSI', label: 'Non-Tax Sale' },
  { code: 'PS', label: 'POS Sale' },
  { code: 'SR', label: 'Tax Return' },
  { code: 'NSR', label: 'Non-Tax Return' },
];
const SALE_CODES = ['SI', 'NSI', 'PS'];
const RETURN_CODES = ['SR', 'NSR'];
const isSaleCode = (c) => SALE_CODES.includes(c);
const isReturnCode = (c) => RETURN_CODES.includes(c);

const STATUS_CONFIG = {
  Posted: { color: '#28A745', bg: '#E8F5E9', label: 'Posted' },
  Draft: { color: '#6C757D', bg: '#F1F3F5', label: 'Draft' },
  Paid: { color: '#1565C0', bg: '#E3F2FD', label: 'Paid' },
  Partial: { color: '#F57C00', bg: '#FFF3E0', label: 'Partial' },
  Cancelled: { color: '#DC3545', bg: '#FFEBEE', label: 'Cancelled' }
};

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDateStr = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getWeekNumber = (dateStr) => {
  const date = new Date(dateStr);
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const diff = date - startOfYear;
  const oneWeek = 604800000;
  return Math.ceil((diff / oneWeek) + 1);
};

const getQuarter = (dateStr) => {
  const month = new Date(dateStr).getMonth();
  return Math.floor(month / 3) + 1;
};

const getGroupKey = (dateStr, groupBy) => {
  if (!dateStr) return 'Unknown';
  const date = new Date(dateStr);
  switch (groupBy) {
    case 'daily':
      return dateStr;
    case 'weekly': {
      const weekNum = getWeekNumber(dateStr);
      return `${date.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
    }
    case 'monthly':
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    case 'quarterly': {
      const quarter = getQuarter(dateStr);
      return `${date.getFullYear()}-Q${quarter}`;
    }
    default:
      return dateStr;
  }
};

const getGroupLabel = (key, groupBy) => {
  if (!key || key === 'Unknown') return 'Unknown';
  switch (groupBy) {
    case 'daily':
      return formatDateStr(key);
    case 'weekly':
      return key;
    case 'monthly': {
      const [year, month] = key.split('-');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[parseInt(month, 10) - 1]} ${year}`;
    }
    case 'quarterly':
      return key;
    default:
      return key;
  }
};

const SaleSummary = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('sale-summary');
  const taxLabel = activeCompany?.taxLabel || 'GST';
  // Customer NTN column (2026-10-28): from the customer master ("CR" in VAT mode).
  const ntnLabel = getTaxTerms(activeCompany).registrationLabel;

  // Data states
  const [transactions, setTransactions] = useState(() => savedMem?.transactions ?? []);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [selectedCustomer, setSelectedCustomer] = useState(() => savedMem?.selectedCustomer ?? '');
  const [selectedSalesperson, setSelectedSalesperson] = useState(() => savedMem?.selectedSalesperson ?? '');
  const [selectedJob, setSelectedJob] = useState(() => savedMem?.selectedJob ?? '');
  // FLW-05: default to posted documents (Posted / Partial / Paid) — Draft and
  // Cancelled are left out unless picked in the Status filter.
  const [selectedStatus, setSelectedStatus] = useState(() => savedMem?.selectedStatus ?? 'Posted');
  // Document-type checkboxes (replaces the old Tax dropdown). Any combination.
  const [docTypes, setDocTypes] = useState(() => savedMem?.docTypes ?? { SI: true, NSI: true, PS: true, SR: true, NSR: true });
  const toggleDocType = (code) => setDocTypes(prev => ({ ...prev, [code]: !prev[code] }));
  const [groupBy, setGroupBy] = useState(() => savedMem?.groupBy ?? 'monthly');
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState(() => savedMem?.selectedRegionId ?? 'all');
  const [selectedAreaId, setSelectedAreaId] = useState(() => savedMem?.selectedAreaId ?? 'all');

  // UI states
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(() => savedMem?.hasSearched ?? false);
  const [sortField, setSortField] = useState(() => savedMem?.sortField ?? 'docDate');
  const [sortDirection, setSortDirection] = useState(() => savedMem?.sortDirection ?? 'desc');

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // Load customers
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const custRef = collection(db, 'companies', activeCompany.id, 'customers');
      const q = query(custRef, orderBy('companyName', 'asc'));
      const snapshot = await getDocs(q);
      setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [activeCompany]);

  // Load employees (salespersons)
  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const empRows = await getEmployeesCached(activeCompany.id);
      setEmployees(empRows.filter(r => r.firstName != null)
        .sort((a, b) => String(a.firstName || '').localeCompare(String(b.firstName || ''))));
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  }, [activeCompany]);

  // Load jobs
  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const jobRows = await getJobsCached(activeCompany.id);
      setJobs(jobRows.filter(r => r.jobName != null)
        .sort((a, b) => String(a.jobName || '').localeCompare(String(b.jobName || ''))));
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  }, [activeCompany]);

  // Load regions
  const loadRegions = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'regions');
      const snapshot = await getDocs(ref);
      setRegions(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  }, [activeCompany]);

  // Load areas
  const loadAreas = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'areas');
      const snapshot = await getDocs(ref);
      setAreas(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears();
    loadCustomers();
    loadEmployees();
    loadJobs();
    loadRegions();
    loadAreas();

    // Set default date range — ONLY on a fresh visit. If we restored a saved view
    // (Back navigation), keep the restored dates.
    if (!savedMem) {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDay.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, [loadFinancialYears, loadCustomers, loadEmployees, loadJobs, loadRegions, loadAreas, savedMem]);

  // Load transactions
  const loadTransactions = async () => {
    if (!activeCompany?.id || !selectedFYId) {
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');
      const conditions = [
        where('tranTypeCode', 'in', ['SI', 'NSI', 'PS', 'SR', 'NSR']),
        where('financialYearId', '==', selectedFYId)
      ];
      // Server-side date bounds (Firebase cost): fetch only the picked range
      // instead of the whole FY on Generate. Empty pickers = whole FY.
      // Client-side date filter below stays as a harmless second pass.
      if (dateFrom) conditions.push(where('docDate', '>=', dateFrom));
      if (dateTo) conditions.push(where('docDate', '<=', dateTo));

      const q = query(transRef, ...conditions);
      const snapshot = await getDocs(q);

      let allTransactions = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

      // Sort by docDate descending
      allTransactions.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));

      setTransactions(allTransactions);

      // Remember this generated view so Back restores it with 0 re-reads.
      saveMemory({
        transactions: allTransactions, hasSearched: true,
        selectedFYId, dateFrom, dateTo, selectedCustomer, selectedSalesperson,
        selectedJob, selectedStatus, docTypes, groupBy,
        selectedRegionId, selectedAreaId, sortField, sortDirection
      });
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Customer map for region/area filtering
  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach(c => { map[c.id] = c; });
    return map;
  }, [customers]);
  const ntnOf = (t) => String(customerMap[t.partyId]?.ntn || t.partyNTN || '').trim();

  // Cascading areas based on selected region
  const filteredAreas = useMemo(() => {
    if (selectedRegionId === 'all') return areas;
    return areas.filter(a => a.regionId === selectedRegionId);
  }, [areas, selectedRegionId]);

  // Filtered transactions
  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(t => (t.docDate || '') <= dateTo);
    }

    // Customer filter
    if (selectedCustomer) {
      filtered = filtered.filter(t => t.partyId === selectedCustomer);
    }

    // Salesperson filter
    if (selectedSalesperson) {
      filtered = filtered.filter(t => t.employeeId === selectedSalesperson);
    }

    // Job filter
    if (selectedJob) {
      filtered = filtered.filter(t => t.jobId === selectedJob);
    }

    // Status filter — "Posted" covers all posted invoices (Posted + Paid + Partial).
    if (selectedStatus !== 'All') {
      const wanted = selectedStatus === 'Posted' ? ['Posted', 'Paid', 'Partial'] : [selectedStatus];
      filtered = filtered.filter(t => wanted.includes(t.status));
    }

    // Document-type checkboxes (Tax Sale / Non-Tax Sale / POS / Tax Return / Non-Tax Return)
    filtered = filtered.filter(t => docTypes[t.tranTypeCode]);

    // Region filter
    if (selectedRegionId !== 'all') {
      filtered = filtered.filter(t => {
        const cust = customerMap[t.partyId];
        return cust && cust.regionId === selectedRegionId;
      });
    }

    // Area filter
    if (selectedAreaId !== 'all') {
      filtered = filtered.filter(t => {
        const cust = customerMap[t.partyId];
        return cust && cust.areaId === selectedAreaId;
      });
    }

    return filtered;
  }, [transactions, dateFrom, dateTo, selectedCustomer, selectedSalesperson, selectedJob, selectedStatus, docTypes, selectedRegionId, selectedAreaId, customerMap]);

  // Sorted transactions
  const sortedTransactions = useMemo(() => {
    const sorted = [...filteredTransactions];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle numeric fields
      if (['totalAmount', 'netAmount', 'totalGST', 'totalWHT', 'totalDiscount'].includes(sortField)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredTransactions, sortField, sortDirection]);

  // Summary calculations
  const summary = useMemo(() => {
    // Only use Posted transactions for accounting figures
    const postedTransactions = filteredTransactions.filter(t => ['Posted', 'Paid', 'Partial'].includes(t.status));

    const siTransactions = postedTransactions.filter(t => isSaleCode(t.tranTypeCode));
    const srTransactions = postedTransactions.filter(t => isReturnCode(t.tranTypeCode));

    const totalSales = siTransactions.reduce((sum, t) => sum + (Number(t.totalAmount) || 0), 0);
    const totalReturns = srTransactions.reduce((sum, t) => sum + (Number(t.totalAmount) || 0), 0);
    const netSales = totalSales - totalReturns;

    const totalGST = postedTransactions.reduce((sum, t) => {
      const gst = Number(t.totalGST) || 0;
      return isReturnCode(t.tranTypeCode) ? sum - gst : sum + gst;
    }, 0);

    const totalDiscount = postedTransactions.reduce((sum, t) => {
      const disc = Number(t.totalDiscount) || 0;
      return isReturnCode(t.tranTypeCode) ? sum - disc : sum + disc;
    }, 0);

    const siCount = siTransactions.length;
    const avgSaleValue = siCount > 0 ? totalSales / siCount : 0;

    return { totalSales, totalReturns, netSales, totalGST, totalDiscount, avgSaleValue };
  }, [filteredTransactions]);

  // Trend chart data (area chart)
  const trendData = useMemo(() => {
    const postedTransactions = filteredTransactions.filter(t => ['Posted', 'Paid', 'Partial'].includes(t.status));
    const grouped = {};

    postedTransactions.forEach(t => {
      const key = getGroupKey(t.docDate, groupBy);
      if (!grouped[key]) {
        grouped[key] = { period: key, sales: 0, returns: 0, net: 0 };
      }
      const amount = Number(t.totalAmount) || 0;
      if (isSaleCode(t.tranTypeCode)) {
        grouped[key].sales += amount;
      } else if (isReturnCode(t.tranTypeCode)) {
        grouped[key].returns += amount;
      }
    });

    // Calculate net for each period
    Object.values(grouped).forEach(g => {
      g.net = g.sales - g.returns;
    });

    // Sort by period key
    return Object.values(grouped).sort((a, b) => a.period.localeCompare(b.period)).map(g => ({
      ...g,
      label: getGroupLabel(g.period, groupBy)
    }));
  }, [filteredTransactions, groupBy]);

  // Top 10 customers bar chart data
  const customerChartData = useMemo(() => {
    const postedTransactions = filteredTransactions.filter(t => ['Posted', 'Paid', 'Partial'].includes(t.status));
    const customerSales = {};

    postedTransactions.forEach(t => {
      const name = t.partyName || 'Unknown';
      if (!customerSales[name]) {
        customerSales[name] = { name, amount: 0 };
      }
      const amount = Number(t.totalAmount) || 0;
      if (isSaleCode(t.tranTypeCode)) {
        customerSales[name].amount += amount;
      } else if (isReturnCode(t.tranTypeCode)) {
        customerSales[name].amount -= amount;
      }
    });

    return Object.values(customerSales)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);
  }, [filteredTransactions]);

  // Salesperson pie chart data
  const salespersonChartData = useMemo(() => {
    const postedTransactions = filteredTransactions.filter(t => ['Posted', 'Paid', 'Partial'].includes(t.status) && isSaleCode(t.tranTypeCode));
    const spSales = {};

    postedTransactions.forEach(t => {
      const name = t.employeeName || 'Unassigned';
      if (!spSales[name]) {
        spSales[name] = { name, value: 0 };
      }
      spSales[name].value += Number(t.totalAmount) || 0;
    });

    return Object.values(spSales).sort((a, b) => b.value - a.value);
  }, [filteredTransactions]);

  // Table totals row
  const tableTotals = useMemo(() => {
    return sortedTransactions.reduce((totals, t) => {
      const sign = isReturnCode(t.tranTypeCode) ? -1 : 1;
      totals.grossAmount += sign * (Number(t.totalAmount) || 0);
      totals.discount += sign * (Number(t.totalDiscount) || 0);
      totals.gst += sign * (Number(t.totalGST) || 0);
      totals.wht += sign * (Number(t.totalWHT) || 0);
      totals.netAmount += sign * (Number(t.netAmount) || 0);
      return totals;
    }, { grossAmount: 0, discount: 0, gst: 0, wht: 0, netAmount: 0 });
  }, [sortedTransactions]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' \u2191' : ' \u2193';
  };

  // Navigate to source document
  const handleDocClick = (transaction) => {
    let route = '/sales/sale-invoice';
    if (isReturnCode(transaction.tranTypeCode)) route = '/sales/sale-return';
    else if (transaction.tranTypeCode === 'PS') route = '/pos';
    navigate(route, { state: { viewTransactionId: transaction.id } });
  };

  // CSV Export
  const handleExport = () => {
    if (sortedTransactions.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Sale Summary Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${dateFrom || 'All'} to ${dateTo || 'All'}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;
    csv += `Total Sales,${summary.totalSales}\n`;
    csv += `Total Returns,${summary.totalReturns}\n`;
    csv += `Net Sales,${summary.netSales}\n`;
    csv += `Total ${taxLabel},${summary.totalGST}\n`;
    csv += `Total Discount,${summary.totalDiscount}\n`;
    csv += `Average Sale Value,${summary.avgSaleValue}\n\n`;
    csv += `Date,Doc #,Type,Customer,Customer ${ntnLabel},Salesperson,Job,Gross Amount,Discount,${taxLabel},WHT,Net Amount,Status,Remarks\n`;

    sortedTransactions.forEach(t => {
      csv += `"${formatDateStr(t.docDate)}","${t.docNumber || ''}","${t.tranTypeCode || ''}",`;
      csv += `"${(t.partyName || '').replace(/"/g, '""')}","${ntnOf(t).replace(/"/g, '""')}","${(t.employeeName || '').replace(/"/g, '""')}",`;
      csv += `"${(t.jobName || '').replace(/"/g, '""')}",`;
      csv += `${t.totalAmount || 0},${t.totalDiscount || 0},${t.totalGST || 0},${t.totalWHT || 0},${t.netAmount || 0},`;
      csv += `"${t.status || ''}","${(t.notes || '').replace(/"/g, '""')}"\n`;
    });

    csv += `\nTotals,,,,,,,${tableTotals.grossAmount},${tableTotals.discount},${tableTotals.gst},${tableTotals.wht},${tableTotals.netAmount}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Sale_Summary_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Sale Summary Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .summary-grid{display:flex;gap:15px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{padding:12px 18px;background:#f9f9f9;border-radius:6px;border-left:3px solid #800020;min-width:120px}
        .summary-label{font-size:10px;color:#666;text-transform:uppercase;margin-bottom:4px}
        .summary-val{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:600}
        .totals-row td{border-top:2px solid #800020}
        .status{padding:2px 6px;border-radius:3px;font-size:10px;font-weight:500}
        .si{color:#2E7D32} .sr{color:#C62828}
        @media print{body{margin:15px}.summary-grid{page-break-inside:avoid}}
      </style></head><body>
      <h1>Sale Summary Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${formatDateStr(dateFrom)} to ${formatDateStr(dateTo)} |
        Records: ${sortedTransactions.length}
      </div>
      <div class="summary-grid">
        <div class="summary-box"><div class="summary-label">Total Sales</div><div class="summary-val">${formatAmount(summary.totalSales)}</div></div>
        <div class="summary-box"><div class="summary-label">Total Returns</div><div class="summary-val">${formatAmount(summary.totalReturns)}</div></div>
        <div class="summary-box"><div class="summary-label">Net Sales</div><div class="summary-val">${formatAmount(summary.netSales)}</div></div>
        <div class="summary-box"><div class="summary-label">Total ${taxLabel}</div><div class="summary-val">${formatAmount(summary.totalGST)}</div></div>
        <div class="summary-box"><div class="summary-label">Total Discount</div><div class="summary-val">${formatAmount(summary.totalDiscount)}</div></div>
        <div class="summary-box"><div class="summary-label">Avg Sale Value</div><div class="summary-val">${formatAmount(summary.avgSaleValue)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Date</th><th>Doc #</th><th>Customer</th><th>${ntnLabel}</th><th>Salesperson</th><th>Job</th>
          <th class="right">Gross Amt</th><th class="right">Discount</th><th class="right">${taxLabel}</th>
          <th class="right">WHT</th><th class="right">Net Amt</th><th>Status</th>
        </tr></thead>
        <tbody>
          ${sortedTransactions.map(t => `
            <tr>
              <td>${formatDateStr(t.docDate)}</td>
              <td><span class="${isReturnCode(t.tranTypeCode) ? 'sr' : 'si'}">${t.docNumber || '-'}</span></td>
              <td>${t.partyName || '-'}</td>
              <td>${ntnOf(t) || '-'}</td>
              <td>${t.employeeName || '-'}</td>
              <td>${t.jobName || '-'}</td>
              <td class="right">${formatAmount(t.totalAmount)}</td>
              <td class="right">${formatAmount(t.totalDiscount)}</td>
              <td class="right">${formatAmount(t.totalGST)}</td>
              <td class="right">${formatAmount(t.totalWHT)}</td>
              <td class="right">${formatAmount(t.netAmount)}</td>
              <td>${t.status || '-'}</td>
            </tr>
          `).join('')}
          <tr class="totals-row">
            <td colspan="6" style="text-align:right">Totals</td>
            <td class="right">${formatAmount(tableTotals.grossAmount)}</td>
            <td class="right">${formatAmount(tableTotals.discount)}</td>
            <td class="right">${formatAmount(tableTotals.gst)}</td>
            <td class="right">${formatAmount(tableTotals.wht)}</td>
            <td class="right">${formatAmount(tableTotals.netAmount)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Custom chart tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{formatCurrency(entry.value)}</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const entry = payload[0];
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{entry.name}</ChartTooltipLabel>
          <ChartTooltipRow>
            <strong>{formatCurrency(entry.value)}</strong>
          </ChartTooltipRow>
        </ChartTooltipBox>
      );
    }
    return null;
  };

  return (
    <PageLayout pageTitle="Sale Summary" breadcrumb="Reports / Sale Reports / Sale Summary">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Customer</Label>
            <CustomerSelect
              customers={customers}
              selectedCustomerId={selectedCustomer}
              onChange={setSelectedCustomer}
              allOption={{ value: '', label: 'All Customers' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Region</Label>
            <Select value={selectedRegionId} onChange={e => { setSelectedRegionId(e.target.value); setSelectedAreaId('all'); }}>
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.regionName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Area</Label>
            <Select value={selectedAreaId} onChange={e => setSelectedAreaId(e.target.value)}>
              <option value="all">All Areas</option>
              {filteredAreas.map(a => (
                <option key={a.id} value={a.id}>{a.areaName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Salesperson</Label>
            <Select value={selectedSalesperson} onChange={e => setSelectedSalesperson(e.target.value)}>
              <option value="">All Salespersons</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.employeeCode ? `${emp.employeeCode} - ` : ''}{emp.fullName || emp.firstName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Job</Label>
            <Select value={selectedJob} onChange={e => setSelectedJob(e.target.value)}>
              <option value="">All Jobs</option>
              {jobs.map(j => (
                <option key={j.id} value={j.id}>
                  {j.jobCode ? `${j.jobCode} - ` : ''}{j.jobName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Status</Label>
            <Select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
              <option value="All">All Statuses</option>
              <option value="Draft">Draft</option>
              <option value="Posted">Posted (incl. Paid / Partial)</option>
              <option value="Cancelled">Cancelled</option>
            </Select>
          </FilterGroup>

          <FilterGroup style={{ gridColumn: 'span 2' }}>
            <Label>Document Types</Label>
            <DocTypeChecks>
              {DOC_TYPE_OPTIONS.map(o => (
                <DocCheck key={o.code}>
                  <input type="checkbox" checked={!!docTypes[o.code]} onChange={() => toggleDocType(o.code)} />
                  <span>{o.label}</span>
                </DocCheck>
              ))}
            </DocTypeChecks>
          </FilterGroup>

          <FilterGroup>
            <Label>Group By</Label>
            <Select value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={sortedTransactions.length === 0}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={sortedTransactions.length === 0}>
            <FiPrinter /> Print
          </Button>
          <Button $primary onClick={loadTransactions} disabled={loading || !selectedFYId}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasSearched && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon $color="#28A745"><FiTrendingUp /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Sales</SummaryLabel>
              <SummaryValue style={{ color: '#28A745' }}>{formatCurrency(summary.totalSales)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#DC3545"><FiTrendingDown /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Returns</SummaryLabel>
              <SummaryValue style={{ color: '#DC3545' }}>{formatCurrency(summary.totalReturns)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.primary.main}><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Net Sales</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.netSales)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent3}><FiPercent /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total {taxLabel}</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalGST)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent2}><FiShoppingCart /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Discount</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalDiscount)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent1}><FiBarChart2 /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Avg Sale Value</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.avgSaleValue)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Data Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiDollarSign /></EmptyIcon>
              <EmptyText>Loading sale transactions...</EmptyText>
            </EmptyState>
          ) : !hasSearched ? (
            <EmptyState>
              <EmptyIcon><FiDollarSign /></EmptyIcon>
              <EmptyText>Select filters and click "Generate Report" to view sale summary</EmptyText>
            </EmptyState>
          ) : sortedTransactions.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiDollarSign /></EmptyIcon>
              <EmptyText>No sale transactions found for the selected criteria</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th $sortable onClick={() => handleSort('docDate')}>Date{getSortIndicator('docDate')}</Th>
                  <Th $sortable onClick={() => handleSort('docNumber')}>Doc #{getSortIndicator('docNumber')}</Th>
                  <Th $sortable onClick={() => handleSort('partyName')}>Customer{getSortIndicator('partyName')}</Th>
                  <Th>{ntnLabel}</Th>
                  <Th $sortable onClick={() => handleSort('employeeName')}>Salesperson{getSortIndicator('employeeName')}</Th>
                  <Th $sortable onClick={() => handleSort('jobName')}>Job{getSortIndicator('jobName')}</Th>
                  <Th $sortable $right onClick={() => handleSort('totalAmount')}>Gross Amount{getSortIndicator('totalAmount')}</Th>
                  <Th $sortable $right onClick={() => handleSort('totalDiscount')}>Discount{getSortIndicator('totalDiscount')}</Th>
                  <Th $sortable $right onClick={() => handleSort('totalGST')}>{taxLabel}{getSortIndicator('totalGST')}</Th>
                  <Th $sortable $right onClick={() => handleSort('totalWHT')}>WHT{getSortIndicator('totalWHT')}</Th>
                  <Th $sortable $right onClick={() => handleSort('netAmount')}>Net Amount{getSortIndicator('netAmount')}</Th>
                  <Th $sortable onClick={() => handleSort('status')}>Status{getSortIndicator('status')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedTransactions.map((t, index) => (
                  <Tr key={t.id || index}>
                    <Td>{formatDateStr(t.docDate)}</Td>
                    <Td>
                      <DocNumber onClick={() => handleDocClick(t)} title="Click to open transaction">
                        <TranTypeTag $type={t.tranTypeCode}>{t.tranTypeCode}</TranTypeTag>
                        {t.docNumber || '-'} <FiExternalLink size={11} />
                      </DocNumber>
                    </Td>
                    <Td>{t.partyName || '-'}</Td>
                    <Td style={{ whiteSpace: 'nowrap' }}>{ntnOf(t) || '-'}</Td>
                    <Td>{t.employeeName || '-'}</Td>
                    <Td>{t.jobName || '-'}</Td>
                    <AmountCell $right $type={isReturnCode(t.tranTypeCode) ? 'return' : 'sale'}>
                      {isReturnCode(t.tranTypeCode) ? '(' : ''}{formatAmount(t.totalAmount)}{isReturnCode(t.tranTypeCode) ? ')' : ''}
                    </AmountCell>
                    <AmountCell $right>{formatAmount(t.totalDiscount)}</AmountCell>
                    <AmountCell $right>{formatAmount(t.totalGST)}</AmountCell>
                    <AmountCell $right>{formatAmount(t.totalWHT)}</AmountCell>
                    <AmountCell $right $type={isReturnCode(t.tranTypeCode) ? 'return' : 'sale'}>
                      {isReturnCode(t.tranTypeCode) ? '(' : ''}{formatAmount(t.netAmount)}{isReturnCode(t.tranTypeCode) ? ')' : ''}
                    </AmountCell>
                    <Td>
                      <StatusBadge $status={t.status}>
                        {STATUS_CONFIG[t.status]?.label || t.status || '-'}
                      </StatusBadge>
                    </Td>
                  </Tr>
                ))}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={6} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Totals ({sortedTransactions.length} records)
                  </Td>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(tableTotals.grossAmount)}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(tableTotals.discount)}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(tableTotals.gst)}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(tableTotals.wht)}</AmountCell>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(tableTotals.netAmount)}</AmountCell>
                  <Td></Td>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasSearched && !loading && filteredTransactions.length > 0 && (
        <ChartsRow>
          {/* Sales Trend Area Chart */}
          <ChartSection $wide>
            <ChartTitle>Sales Trend ({groupBy.charAt(0).toUpperCase() + groupBy.slice(1)})</ChartTitle>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trendData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#28A745" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#28A745" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="returnsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#DC3545" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#DC3545" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="netGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={colors.primary.main} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={colors.primary.main} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <YAxis tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => formatNumber(v, { compact: true })} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="sales" name="Sales" stroke="#28A745" fill="url(#salesGradient)" strokeWidth={2} />
                  <Area type="monotone" dataKey="returns" name="Returns" stroke="#DC3545" fill="url(#returnsGradient)" strokeWidth={2} />
                  <Area type="monotone" dataKey="net" name="Net Sales" stroke={colors.primary.main} fill="url(#netGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No trend data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Top 10 Customers Bar Chart */}
          <ChartSection>
            <ChartTitle>Top 10 Customers by Sales</ChartTitle>
            {customerChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={customerChartData} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => formatNumber(v, { compact: true })} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke={colors.text.secondary} width={120} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="amount" name="Net Sales" fill={colors.chart.primary} radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No customer data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Salesperson Pie Chart */}
          <ChartSection>
            <ChartTitle>Sales by Salesperson</ChartTitle>
            {salespersonChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={salespersonChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {salespersonChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No salesperson data available</ChartEmpty>
            )}
          </ChartSection>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const DocTypeChecks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  padding: 8px 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  background: white;
`;

const DocCheck = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: ${colors.text.primary};
  cursor: pointer;
  white-space: nowrap;
  input { cursor: pointer; }
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 9px 18px;
  border: ${props => props.$primary ? 'none' : `1px solid ${colors.neutral[300]}`};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  white-space: nowrap;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;

  ${props => props.$wide && `
    grid-column: 1 / -1;
  `}
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const ChartEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 10px 14px;
  box-shadow: ${colors.shadows.md};
`;

const ChartTooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
  }
`;

const ChartTooltipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.$sortable && `
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};

  td {
    padding: 14px;
  }

  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
  color: ${props => {
    if (props.$type === 'return') return '#DC3545';
    if (props.$type === 'sale') return colors.text.primary;
    return colors.text.primary;
  }};
`;

const DocNumber = styled.span`
  font-weight: 500;
  color: ${colors.primary.main};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s;
  font-size: 13px;

  &:hover {
    background: ${colors.primary.main}15;
    text-decoration: underline;
  }

  svg {
    opacity: 0.5;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const TranTypeTag = styled.span`
  display: inline-block;
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  background: ${props => props.$type === 'SR' ? '#FFEBEE' : '#E8F5E9'};
  color: ${props => props.$type === 'SR' ? '#C62828' : '#2E7D32'};
  margin-right: 4px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => STATUS_CONFIG[props.$status]?.bg || colors.neutral[100]};
  color: ${props => STATUS_CONFIG[props.$status]?.color || colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default SaleSummary;
